import React from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Divider,
  Stack,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

const stepData = [
  {
    title: "1. Get nominated.",
    points: [
      "Self–nominate or ask a colleague to nominate you. Remember, if you aren't nominated you can't win!",
      "DON'T: Pass up an award because you think you won't win.",
    ],
  },
  {
    title: "2. Identify the proper award.",
    points: [
      "Check previous award winners. Is their work similar to yours or that of your nominee?",
      "Check award timelines. Make sure to give yourself ample time to put together the best application.",
      "Think about the achievements that are the most important for this award. Will it highlight your strengths?",
      "Find out who has been/will be on the award committee. If you know a current or past committee member, contact that person to ask about the award process.",
    ],
  },
  {
    title: "3. Learn about the nomination process.",
    points: [
      "Volunteer to serve on an awards committee.",
      "Look into unacknowledged rules (e.g., although the award language doesn't say so, some awards may emphasize a major discovery while others may require sustained scholarly contributions over a whole career).",
    ],
  },
  {
    title: "4. Decide on the most effective nominator or secondary nominator",
    points: [
      "How many nominators/seconders will be needed?",
      "Consider award experience. People who have previously received the award or been on the selection committee are both good choices.",
      "Consider prestige. Ask arbiters of quality in the field.",
      "Consider personal commitment: Select people who are excited to endorse the candidate's success!",
      "Identify the specific role the nominators will play. Will they highlight your work or your character?",
      "DON'T: Ask someone who may see the nominee as competition.",
      "DON'T: Ask someone who may not follow-through.",
      "DON'T: Ask someone about whom you have doubts.",
    ],
  },
  {
    title: "5. Submit the award.",
    points: [
      "FOLLOW DIRECTIONS. Award applications may be discarded if you fail to abide by the instructions.",
      "Confirm receipt of the materials.",
    ],
  },
  {
    title: "6. BE AWARE -- Implicit bias is everywhere.",
    points: [
      "Language and semantics carry weight. Men's themes are often competitive; women's are nurturing. The masculine style may make for a stronger application.",
      "Awards committees may favor male writing due to implicit bias. Awareness is important.",
      "See AWIS webinars. Supported by NSF ADVANCE Grant #0930073.",
    ],
  },
  {
    title: "7. Resubmit the award.",
    points: [
      'BE PERSISTENT! Award recipients often have to "wait their turn" on a list.',
      "Find out whether the nomination will be carried forward. If not, find out what else is needed.",
      "DON'T: Be discouraged!",
    ],
  },
];

const Tips = () => {
  return (
    <Container maxWidth="md" sx={{ mt: 10, mb: 5 }}>
      <Card
        elevation={2}
        sx={{ p: { xs: 2, sm: 3 }, backgroundColor: "#f8f9fa" }}
      >
        <CardContent>
          <Typography variant="body1" fontStyle="italic" gutterBottom>
            Here are some helpful tips for potential nominators and nominees
            based on our experience with multiple organizations, award
            committees, and review of the literature on gender equity and
            stereotyping, as well as our analysis of over awards and award
            recipients. A useful source of detailed information is the product
            of an NSF sponsored program,{" "}
            <strong>
              Advancing Ways of Awarding Recognition in Disciplinary Societies
            </strong>
            , a collaboration with AWIS.{" "}
            <em>
              (
              <a
                href="https://agupubs.onlinelibrary.wiley.com/doi/10.1029/2011EO470002"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--primary-color)", textDecoration: "underline" }}
              >
                Click here for Collaboration Report
              </a>
              )
            </em>
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" fontWeight="bold" gutterBottom>
            Diversifying Awards → 7 Steps
          </Typography>

          <Stack spacing={3} mt={2}>
            {stepData.map((step, index) => (
              <Box key={index} sx={{ pl: 1 }}>
                <Typography
                  variant="subtitle1"
                  fontWeight="bold"
                  display="flex"
                  alignItems="center"
                >
                  <CheckCircleIcon
                    sx={{ fontSize: 20, mr: 1, color: "var(--primary-color)" }}
                  />
                  {step.title}
                </Typography>
                {step.points.map((point, i) => (
                  <Typography
                    key={i}
                    variant="body2"
                    sx={{
                      ml: 4,
                      mt: 0.5,
                      color: point.startsWith("DON'T")
                        ? "error.main"
                        : "text.primary",
                    }}
                  >
                    {point}
                  </Typography>
                ))}
              </Box>
            ))}
          </Stack>

          <Divider sx={{ my: 4 }} />

          <Typography variant="body2" color="text.secondary">
            Suggestions are welcome. Send us your questions and concerns to
            facilitate preparation of this section.
          </Typography>
        </CardContent>
      </Card>
    </Container>
  );
};

export default Tips;
